import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Search, ChevronLeft, ChevronRight, Home, MapPin, SearchX, View } from "lucide-react"
import { format } from "date-fns"

import { api } from "@/lib/api"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"

interface AdminProperty {
    id: string
    name: string
    type: string | null
    city: string | null
    country_code: string | null
    status: string
    monthly_rent: string | null
    currency: string
    created_at: string
    owner_id: string
    owner_email: string
    owner_name: string | null
}

export default function AdminPropertiesPage() {
    const [page, setPage] = useState(1)
    const [search, setSearch] = useState("")

    const { data, isLoading } = useQuery({
        queryKey: ["admin", "properties", page],
        queryFn: async () => {
            const { data } = await api.get(`/api/admin/properties?page=${page}&limit=15`)
            return data.data
        },
    })

    const properties: AdminProperty[] = data?.properties ?? []
    const pagination = data?.pagination

    const filtered = search
        ? properties.filter(
            (p) =>
                p.name.toLowerCase().includes(search.toLowerCase()) ||
                (p.city ?? "").toLowerCase().includes(search.toLowerCase()) ||
                p.owner_email.toLowerCase().includes(search.toLowerCase())
        )
        : properties

    return (
        <div className="p-8 max-w-[1400px] mx-auto flex flex-col h-[calc(100vh-theme(spacing.16))]">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Global Properties</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        {pagination?.total ?? 0} total properties registered on the platform
                    </p>
                </div>

                <div className="flex items-center gap-3">
                    <div className="relative w-full sm:w-72">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search by name, city or owner..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-9 h-10 rounded-xl bg-card border-border shadow-sm"
                        />
                    </div>
                </div>
            </div>

            <div className="flex-1 bg-card border border-border rounded-2xl shadow-sm overflow-hidden flex flex-col">
                <div className="flex-1 overflow-auto">
                    <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-card/95 backdrop-blur-sm z-10 border-b border-border shadow-sm">
                            <tr>
                                <th className="text-left px-6 py-4 font-semibold text-muted-foreground uppercase text-xs tracking-wider">Property</th>
                                <th className="text-left px-6 py-4 font-semibold text-muted-foreground uppercase text-xs tracking-wider">Type & Location</th>
                                <th className="text-left px-6 py-4 font-semibold text-muted-foreground uppercase text-xs tracking-wider">Owner</th>
                                <th className="text-left px-6 py-4 font-semibold text-muted-foreground uppercase text-xs tracking-wider">Status & Rent</th>
                                <th className="text-left px-6 py-4 font-semibold text-muted-foreground uppercase text-xs tracking-wider">Registered</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {isLoading ? (
                                Array.from({ length: 8 }).map((_, i) => (
                                    <tr key={i}>
                                        <td className="px-6 py-4"><Skeleton className="h-10 w-48 rounded-xl" /></td>
                                        <td className="px-6 py-4"><Skeleton className="h-10 w-40 rounded-xl" /></td>
                                        <td className="px-6 py-4"><Skeleton className="h-10 w-32 rounded-xl" /></td>
                                        <td className="px-6 py-4"><Skeleton className="h-10 w-24 rounded-xl" /></td>
                                        <td className="px-6 py-4"><Skeleton className="h-4 w-24" /></td>
                                    </tr>
                                ))
                            ) : filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-16 text-center text-muted-foreground">
                                        <div className="flex flex-col items-center justify-center">
                                            <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-3">
                                                <SearchX className="h-6 w-6" />
                                            </div>
                                            <p className="text-base font-medium text-foreground">No properties found</p>
                                            <p className="text-sm">We couldn't find anything matching your search query.</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filtered.map((property) => (
                                    <tr
                                        key={property.id}
                                        className="group hover:bg-muted/40 transition-colors"
                                    >
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="h-10 w-10 rounded-xl bg-orange-500/10 flex items-center justify-center shrink-0 border border-orange-500/20">
                                                    <Home className="h-5 w-5 text-orange-600" />
                                                </div>
                                                <div>
                                                    <p className="font-semibold text-foreground group-hover:text-primary transition-colors">
                                                        {property.name}
                                                    </p>
                                                    <p className="text-xs text-muted-foreground font-mono mt-0.5" title={property.id}>
                                                        ID: {property.id.split("-")[0]}...
                                                    </p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div>
                                                {property.type ? (
                                                    <Badge variant="outline" className="capitalize text-xs font-medium bg-background mb-1">
                                                        {property.type.replace("_", " ")}
                                                    </Badge>
                                                ) : (
                                                    <span className="text-muted-foreground text-xs block mb-1">—</span>
                                                )}
                                                <div className="flex items-center text-xs text-muted-foreground">
                                                    <MapPin className="h-3 w-3 mr-1" />
                                                    {property.city ? `${property.city}, ${property.country_code}` : "Location not set"}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div>
                                                <p className="font-medium text-sm">{property.owner_name || "—"}</p>
                                                <p className="text-xs text-muted-foreground mt-0.5">{property.owner_email}</p>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div>
                                                <Badge variant="secondary" className={cn(
                                                    "capitalize text-xs mb-1",
                                                    property.status === "available" && "bg-emerald-100 text-emerald-800 hover:bg-emerald-100",
                                                    property.status === "rented" && "bg-blue-100 text-blue-800 hover:bg-blue-100",
                                                    property.status === "maintenance" && "bg-amber-100 text-amber-800 hover:bg-amber-100"
                                                )}>
                                                    {property.status}
                                                </Badge>
                                                {property.monthly_rent ? (
                                                    <p className="text-xs font-medium mt-1">
                                                        {property.currency} {parseFloat(property.monthly_rent).toFixed(2)} / mo
                                                    </p>
                                                ) : (
                                                    <p className="text-xs text-muted-foreground mt-1">—</p>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-muted-foreground text-sm font-medium">
                                            {format(new Date(property.created_at), "MMM d, yyyy")}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                {pagination && pagination.total_pages > 1 && (
                    <div className="bg-muted/30 border-t border-border px-6 py-3 flex items-center justify-between">
                        <p className="text-sm font-medium text-muted-foreground">
                            Showing page <span className="text-foreground">{pagination.page}</span> of <span className="text-foreground">{pagination.total_pages}</span>
                        </p>
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-8 rounded-lg shadow-sm"
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                disabled={page === 1}
                            >
                                <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-8 rounded-lg shadow-sm"
                                onClick={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
                                disabled={page === pagination.total_pages}
                            >
                                Next <ChevronRight className="h-4 w-4 ml-1" />
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
